import { Copy, ExternalLink, MapPin, MapPinOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { DeviceBadge } from '@/components/DeviceBadge';
import { loadConfig } from '@/lib/config';
import type { Room } from '@/lib/types';

interface Props {
  roomId: string;
  joinCode?: string | null;
  anchorPose?: Room['anchorPose'];
  onClearAnchor?: () => void;
  onCopy?: (url: string) => void;
}

/**
 * Join info for the WebXR passthrough headset client (xr.html) — no QR
 * code here (unlike MobileArCard): scanning a QR from inside a headset
 * that's already on your face doesn't work well in practice (confirmed
 * on real Quest 3 hardware), so the 5-digit join code — read out loud,
 * typed on xr.html's own on-screen keypad — is the real path in, not a
 * fallback for it. The direct URL is still offered for anyone who'd
 * rather open it via a bookmark/shared link than type a code.
 */
export function HeadsetCard({ roomId, joinCode, anchorPose, onClearAnchor, onCopy }: Props) {
  const [xrUrl, setXrUrl] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cfg = await loadConfig();
      const url = `${cfg.syncBaseURL}/xr.html?room=${roomId}`;
      if (!cancelled) setXrUrl(url);
    })();
    return () => {
      cancelled = true;
    };
  }, [roomId]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          <DeviceBadge type="xr_headset" variant="label" className="text-foreground" />
        </CardTitle>
        <CardDescription>Passthrough AR — Quest 3 or Vision Pro. No marker needed.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {joinCode ? (
          <div className="flex flex-col items-center gap-1 rounded-md bg-muted py-4">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Join code</span>
            <span className="font-mono text-3xl font-bold tracking-widest text-primary">{joinCode}</span>
            <span className="px-4 text-center text-[11px] text-muted-foreground">
              Read this out — typed on xr.html&apos;s own keypad, no URL needed.
            </span>
          </div>
        ) : (
          <div className="rounded-md border border-dashed py-4 text-center text-xs text-muted-foreground">
            No join code yet — reopen this room to mint one.
          </div>
        )}

        <div className="w-full break-all rounded-md bg-muted px-3 py-2 text-center text-xs text-muted-foreground">
          {xrUrl || '…'}
        </div>

        <div className="flex w-full gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => {
              if (!xrUrl) return;
              void navigator.clipboard.writeText(xrUrl);
              onCopy?.(xrUrl);
            }}
          >
            <Copy className="h-3.5 w-3.5" /> Copy link
          </Button>
          <Button variant="outline" size="sm" className="flex-1" asChild>
            <a href={xrUrl} target="_blank" rel="noreferrer">
              <ExternalLink className="h-3.5 w-3.5" /> Open
            </a>
          </Button>
        </div>

        <div className="flex items-center justify-between rounded-md border px-3 py-2 text-xs">
          <span className="flex items-center gap-2 text-muted-foreground">
            {anchorPose ? <MapPin className="h-3.5 w-3.5 text-primary" /> : <MapPinOff className="h-3.5 w-3.5" />}
            {anchorPose ? 'Room anchor calibrated' : 'Room anchor not calibrated'}
          </span>
          {anchorPose && (
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={onClearAnchor}>
              Clear
            </Button>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground">
          {anchorPose
            ? 'A headset touched a physical spot — every other headset in the room converges on it too.'
            : 'A headset user can touch a physical spot (e.g. the printed marker) via the in-world Anchor button to line everyone up on the same real-world point.'}
        </p>
      </CardContent>
    </Card>
  );
}
